import type { ArenaConfig } from "../config/gameplay-config";
import type { PlayerMovementState } from "./player-movement";

export interface ArenaBoundaryContact {
  readonly bottom: boolean;
  readonly left: boolean;
  readonly right: boolean;
  readonly top: boolean;
}

export interface ArenaBoundaryResult {
  readonly contact: ArenaBoundaryContact;
  readonly player: PlayerMovementState;
}

interface ArenaBoundedSnake {
  readonly collision: { readonly radius: number };
  readonly directionRadians: number;
  readonly position: { readonly x: number; readonly y: number };
}

export interface SnakeArenaBoundaryResult<TSnake extends ArenaBoundedSnake> {
  readonly contact: ArenaBoundaryContact;
  readonly snake: TSnake;
}

export interface PlayableArenaBounds {
  readonly maximumX: number;
  readonly maximumY: number;
  readonly minimumX: number;
  readonly minimumY: number;
}

export const createPlayableArenaBounds = (
  arena: ArenaConfig,
  collisionRadius: number,
): PlayableArenaBounds => {
  if (!Number.isFinite(collisionRadius) || collisionRadius < 0) {
    throw new Error("Arena collision radius must be non-negative and finite");
  }
  const minimumX = arena.boundaryInset + collisionRadius;
  const minimumY = arena.boundaryInset + collisionRadius;
  const maximumX = arena.width - arena.boundaryInset - collisionRadius;
  const maximumY = arena.height - arena.boundaryInset - collisionRadius;
  if (maximumX < minimumX || maximumY < minimumY) {
    throw new Error("Arena must contain the collision circle");
  }
  return { maximumX, maximumY, minimumX, minimumY };
};

const emptyContact = (): ArenaBoundaryContact => ({
  bottom: false,
  left: false,
  right: false,
  top: false,
});

export const hasArenaBoundaryContact = (
  contact: ArenaBoundaryContact,
): boolean => contact.left || contact.right || contact.top || contact.bottom;

export const resolveSnakeArenaBoundary = <TSnake extends ArenaBoundedSnake>(
  snake: TSnake,
  arena: ArenaConfig,
): SnakeArenaBoundaryResult<TSnake> => {
  const bounds = createPlayableArenaBounds(arena, snake.collision.radius);
  const contact = {
    bottom: snake.position.y >= bounds.maximumY,
    left: snake.position.x <= bounds.minimumX,
    right: snake.position.x >= bounds.maximumX,
    top: snake.position.y <= bounds.minimumY,
  };
  if (!hasArenaBoundaryContact(contact)) {
    return { contact: emptyContact(), snake };
  }

  const horizontalDirection = Math.cos(snake.directionRadians);
  const verticalDirection = Math.sin(snake.directionRadians);
  const redirectedHorizontal =
    (contact.left && horizontalDirection < 0) ||
    (contact.right && horizontalDirection > 0)
      ? -horizontalDirection
      : horizontalDirection;
  const redirectedVertical =
    (contact.top && verticalDirection < 0) ||
    (contact.bottom && verticalDirection > 0)
      ? -verticalDirection
      : verticalDirection;
  const redirected =
    redirectedHorizontal !== horizontalDirection ||
    redirectedVertical !== verticalDirection;
  const minimumX = bounds.minimumX + arena.boundaryPushDistance;
  const maximumX = bounds.maximumX - arena.boundaryPushDistance;
  const minimumY = bounds.minimumY + arena.boundaryPushDistance;
  const maximumY = bounds.maximumY - arena.boundaryPushDistance;
  return {
    contact,
    snake: {
      ...snake,
      directionRadians: redirected
        ? Math.atan2(redirectedVertical, redirectedHorizontal)
        : snake.directionRadians,
      position: {
        x: Math.min(maximumX, Math.max(minimumX, snake.position.x)),
        y: Math.min(maximumY, Math.max(minimumY, snake.position.y)),
      },
    },
  };
};

export const resolveArenaBoundary = (
  player: PlayerMovementState,
  arena: ArenaConfig,
): ArenaBoundaryResult => {
  const result = resolveSnakeArenaBoundary(player, arena);
  return { contact: result.contact, player: result.snake };
};
